import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '../index';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ScoutRunRow {
  id: number;
  uuid: string;
  session_id: number;
  session_uuid: string | null;
  chat_message_id: number | null;
  model: string;
  was_used: boolean;
  analysis_summary: string | null;
  analysis_json: string | null;
  prompt_tokens: number;
  completion_tokens: number;
  duration_ms: number;
  error: string | null;
  created_at: string;
  ticket_title: string | null;
  ticket_uuid: string | null;
  service_name: string | null;
  service_uuid: string | null;
}

export interface ScoutStats {
  total_runs: number;
  successful_runs: number;
  failed_runs: number;
  avg_duration_ms: number;
  avg_prompt_tokens: number;
  avg_completion_tokens: number;
  total_prompt_tokens: number;
  total_completion_tokens: number;
  high_confidence: number;
  medium_confidence: number;
  low_confidence: number;
}

export interface ScoutRunsResponse {
  success: boolean;
  data: ScoutRunRow[];
  stats: ScoutStats | null;
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface ScoutRunResponse {
  success: boolean;
  data: ScoutRunRow;
}

export interface ScoutModelsResponse {
  success: boolean;
  data: string[];
}

export interface ScoutRunsParams {
  page?: number;
  limit?: number;
  search?: string;
  wasUsed?: '' | 'true' | 'false';
  model?: string;
}

// ─── API slice ────────────────────────────────────────────────────────────────

export const aiScoutApi = createApi({
  reducerPath: 'aiScoutApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/admin/ai-scout',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['ScoutRuns'],
  endpoints: (builder) => ({
    listScoutRuns: builder.query<ScoutRunsResponse, ScoutRunsParams | void>({
      query: (params = {}) => ({
        url: '/runs',
        params: {
          page:    params?.page    ?? 1,
          limit:   params?.limit   ?? 50,
          search:  params?.search  ?? '',
          wasUsed: params?.wasUsed ?? '',
          model:   params?.model   ?? '',
        },
      }),
      providesTags: ['ScoutRuns'],
    }),
    getScoutRun: builder.query<ScoutRunResponse, string>({
      query: (uuid) => `/runs/${uuid}`,
    }),
    listScoutModels: builder.query<ScoutModelsResponse, void>({
      query: () => '/models',
    }),
  }),
});

export const {
  useListScoutRunsQuery,
  useGetScoutRunQuery,
  useListScoutModelsQuery,
} = aiScoutApi;
